import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import kycService from "../../../services/api/kyc.service";
import { Edit2 } from "lucide-react";

const BankDetailsForm = ({ onComplete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountType, setAccountType] = useState("savings"); // NEW: Added accountType field

  const [validationErrors, setValidationErrors] = useState({
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    accountName: "",
    accountType: "",
  });
  const [kycStatus, setKycStatus] = useState(null);

  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const profileData = await kycService.getProfile();

        if (
          profileData &&
          profileData.kycDetails &&
          profileData.kycDetails.bankDetails
        ) {
          setBankName(profileData.kycDetails.bankDetails.bankName || "");
          setAccountNumber(
            profileData.kycDetails.bankDetails.accountNumber || ""
          );
          setIfscCode(profileData.kycDetails.bankDetails.ifscCode || "");
          setAccountName(profileData.kycDetails.bankDetails.accountName || "");
          // NEW: Load existing accountType
          setAccountType(
            profileData.kycDetails.bankDetails.accountType || "savings"
          );
        }
        // Set KYC lock status
        const status = profileData?.kycDetails?.verificationStatus || null;
        setKycStatus(status);
      } catch (error) {
        console.error("Error loading existing bank data:", error);
      }
    };

    loadExistingData();
  }, []);

  const validateAccountName = (value) => {
    if (!value.trim()) {
      return "Account holder name is required";
    }
    if (value.length < 3) {
      return "Name must be at least 3 characters";
    }
    if (!/^[A-Za-z\s.]+$/.test(value)) {
      return "Only letters, spaces and periods are allowed";
    }
    return "";
  };

  const validateBankName = (value) => {
    if (!value.trim()) {
      return "Bank name is required";
    }
    if (value.length < 3) {
      return "Bank name must be at least 3 characters";
    }
    if (!/^[A-Za-z\s]+$/.test(value)) {
      return "Only letters and spaces are allowed for bank name";
    }
    return "";
  };

  const validateAccountNumber = (value) => {
    if (!value.trim()) {
      return "Account number is required";
    }
    if (!/^\d+$/.test(value)) {
      return "Please enter only digits (0-9)";
    }
    if (value.length < 9 || value.length > 18) {
      return "Account number must be between 9 and 18 digits";
    }
    return "";
  };

  const validateIfscCode = (value) => {
    if (!value.trim()) {
      return "IFSC code is required";
    }
    // 11 characters: 4 letters + 0 + 6 alphanumeric
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
      return "Format: 4 letters, '0', then 6 digits/letters (e.g. SBIN0001234)";
    }
    return "";
  };

  // NEW: Validation for account type
  const validateAccountType = (value) => {
    const validTypes = ["savings", "current", "nre", "nro"];
    if (!validTypes.includes(value)) {
      return "Please select a valid account type";
    }
    return "";
  };

  const handleAccountNameChange = (e) => {
    const value = e.target.value;
    setAccountName(value);
    setValidationErrors({
      ...validationErrors,
      accountName: validateAccountName(value),
    });
  };

  const handleBankNameChange = (e) => {
    const value = e.target.value;
    setBankName(value);
    setValidationErrors({
      ...validationErrors,
      bankName: validateBankName(value),
    });
  };

  const handleAccountNumberChange = (e) => {
    const value = e.target.value;
    setAccountNumber(value);
    setValidationErrors({
      ...validationErrors,
      accountNumber: validateAccountNumber(value),
    });
  };

  const handleIfscCodeChange = (e) => {
    // Auto-uppercase, hard-limit 11 chars. We don't strip chars here to allow the validation error to show.
    const value = e.target.value.toUpperCase().slice(0, 11);
    setIfscCode(value);
    setValidationErrors({
      ...validationErrors,
      ifscCode: validateIfscCode(value),
    });
  };

  // NEW: Handler for account type change
  const handleAccountTypeChange = (e) => {
    const value = e.target.value;
    setAccountType(value);
    setValidationErrors({
      ...validationErrors,
      accountType: validateAccountType(value),
    });
  };

  const validateForm = () => {
    const bankNameError = validateBankName(bankName);
    const accountNumberError = validateAccountNumber(accountNumber);
    const ifscCodeError = validateIfscCode(ifscCode);
    const accountNameError = validateAccountName(accountName);
    const accountTypeError = validateAccountType(accountType); // NEW: Validate account type

    setValidationErrors({
      bankName: bankNameError,
      accountNumber: accountNumberError,
      ifscCode: ifscCodeError,
      accountName: accountNameError,
      accountType: accountTypeError, // NEW: Set account type error
    });

    return (
      !bankNameError &&
      !accountNumberError &&
      !ifscCodeError &&
      !accountNameError &&
      !accountTypeError // NEW: Include account type in validation
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use the new processStep method for step 3 (bank details)
      const formData = {
        bankName,
        accountNumber,
        ifscCode,
        accountName,
        accountType, // NEW: Include accountType in form data
      };

      // Call processStep method with step 3 for bank details
      const response = await kycService.processStep(3, formData);

      if (
        response.success === true ||
        (typeof response === "object" && response.status === "success") ||
        (typeof response === "string" && response.includes("successfully"))
      ) {
        toast.success("Bank details updated successfully");

        if (typeof onComplete === "function") {
          onComplete();
        } else {
          navigate("/kyc");
        }
      } else {
        const errorMessage =
          typeof response === "object" && response.message
            ? response.message
            : "Failed to update bank details";
        throw new Error(errorMessage);
      }
    } catch (err) {
      if (err.message && err.message.includes("successfully")) {
        toast.success("Bank details updated successfully");

        if (typeof onComplete === "function") {
          onComplete();
        } else {
          navigate("/kyc");
        }
      } else {
        const errorMessage = err.message || "Failed to update bank details";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Lock state ────────────────────────────────────────────────────────────────
  const LOCKED_STATUSES = ["pending", "verified", "in-review"];
  const isLocked = LOCKED_STATUSES.includes(kycStatus);
  const statusBannerConfig = {
    pending: { bg: "bg-yellow-50 border-yellow-300", icon: "⏳", text: "Your KYC is under review.", sub: "Editing is disabled until admin completes the review." },
    verified: { bg: "bg-green-50 border-green-400", icon: "✅", text: "Your KYC is verified.", sub: "No changes are allowed." },
    "in-review": { bg: "bg-blue-50 border-blue-400", icon: "🔍", text: "Your KYC is being reviewed.", sub: "Editing is temporarily locked." },
  };
  const banner = statusBannerConfig[kycStatus];
  // ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow relative mt-15">
      <h2 className="text-2xl font-bold mb-6">Bank Details</h2>

      {/* Lock Banner */}
      {isLocked && banner && (
        <div className={`mb-6 flex items-start gap-3 border rounded-xl p-4 ${banner.bg}`}>
          <span className="text-2xl">{banner.icon}</span>
          <div>
            <p className="font-semibold text-gray-800">{banner.text}</p>
            <p className="text-sm text-gray-600 mt-0.5">{banner.sub}</p>
          </div>
        </div>
      )}

      {isLocked ? (
        /* Read-only view */
        <div className="space-y-4">
          {[
            { label: "Account Holder Name", value: accountName },
            { label: "Bank Name", value: bankName },
            { label: "Account Number", value: accountNumber },
            { label: "Account Type", value: accountType },
            { label: "IFSC Code", value: ifscCode },
          ].map(({ label, value }) => (
            <div key={label}>
              <label className="block text-gray-500 text-sm font-medium mb-1">{label}</label>
              <div className="w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-700 min-h-[38px]">
                {value || <span className="text-gray-400 italic">Not provided</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Editable form */
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Account Holder Name *
            </label>
            <input
              type="text"
              value={accountName}
              onChange={handleAccountNameChange}
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                validationErrors.accountName ? "border-red-500" : "border-gray-300"
              }`}
              required
            />
            {validationErrors.accountName && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.accountName}</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Bank Name *
            </label>
            <input
              type="text"
              value={bankName}
              onChange={handleBankNameChange}
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                validationErrors.bankName ? "border-red-500" : "border-gray-300"
              }`}
              required
            />
            {validationErrors.bankName && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.bankName}</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Account Number *
            </label>
            <input
              type="text"
              value={accountNumber}
              onChange={handleAccountNumberChange}
              placeholder="e.g. 123456789012"
              inputMode="numeric"
              maxLength={18}
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                validationErrors.accountNumber ? "border-red-500 shadow-sm shadow-red-100" : "border-gray-300 focus:border-blue-400 focus:ring-blue-100"
              } transition-all duration-200`}
              required
            />
            {validationErrors.accountNumber && (
              <p className="text-red-500 text-xs mt-1 font-medium">{validationErrors.accountNumber}</p>
            )}
            <p className="text-gray-400 text-[10px] mt-1 italic">Note: Enter digits only without spaces or symbols</p>
          </div>

          {/* Account Type Field */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Account Type *
            </label>
            <select
              value={accountType}
              onChange={handleAccountTypeChange}
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                validationErrors.accountType ? "border-red-500" : "border-gray-300"
              }`}
              required
            >
              <option value="">Select Account Type</option>
              <option value="savings">Savings</option>
              <option value="current">Current</option>
              <option value="nre">NRI - NRE (Non-Resident External)</option>
              <option value="nro">NRI - NRO (Non-Resident Ordinary)</option>
            </select>
            {validationErrors.accountType && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.accountType}</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              IFSC Code *
            </label>
            <input
              type="text"
              value={ifscCode}
              onChange={handleIfscCodeChange}
              maxLength={11}
              placeholder="E.g. SBIN0001234"
              className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 ${
                validationErrors.ifscCode ? "border-red-500" : "border-gray-300"
              }`}
              required
            />
            {validationErrors.ifscCode && (
              <p className="text-red-500 text-xs mt-1 font-medium">{validationErrors.ifscCode}</p>
            )}
            <p className="text-gray-400 text-[10px] mt-1 italic">Example: HDFC0001234, ICIC0000123</p>
          </div>

          {error && <div className="mb-4 text-red-500 text-sm">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Updating..." : "Update Details"}
          </button>
        </form>
      )}
    </div>
  );
};

export default BankDetailsForm;
